import React, { useEffect, useRef, useState } from 'react';
import {
    Modal,
    TouchableOpacity,
    View,
    Text,
    TextInput,
    Animated,
    StyleSheet,
} from 'react-native';
import PINDots from './PINDots';
import PinKeypad, { PIN_INPUT_PROPS } from '../../../web/PinKeypad';
import { usePIN } from '../../contexts/PINContext';
import { COLORS } from '../../theme/themeTokens';

// PINConfirmModal — переиспользуемый модальный popup для подтверждения
// чувствительных операций (Withdraw / Card Deposit и т.п.) вводом 4-значного
// PIN-кода.
//
// Логика и визуал точно копируют PIN-блок из CardScreen ("Card details")
// — той же auto-submit на 4-й цифре, shake-анимация на неверном вводе,
// тот же overlay-touchable для закрытия. Биометрия НЕ копируется (отдельная
// итерация), lockout-after-N-fails тоже не реализован — CardScreen его не
// имеет, остаёмся 1-в-1.
//
// Caller отвечает за проверку `isPINSetup`/`isPINSkipped` ДО открытия:
// если PIN не настроен — вызывайте onSuccess() сразу, не показывая модалку.

interface PINConfirmModalProps {
    visible: boolean;
    /** Заголовок модалки. Пример: "Confirm withdrawal". */
    title: string;
    /** Подзаголовок — детали операции. Пример: "100 USDT → 0xABC...DEF". */
    subtitle: string;
    /** PIN корректен → выполнить операцию. Caller сам закрывает модалку. */
    onSuccess: () => void;
    /** Юзер тапнул Cancel или overlay → ничего не делаем. */
    onCancel: () => void;
}

const PIN_LENGTH = 4;

const PINConfirmModal: React.FC<PINConfirmModalProps> = ({
    visible,
    title,
    subtitle,
    onSuccess,
    onCancel,
}) => {
    const [pin, setPin] = useState('');
    const [error, setError] = useState<string | null>(null);
    const inputRef = useRef<TextInput>(null);
    const shakeAnim = useRef(new Animated.Value(0)).current;
    const { verifyPIN } = usePIN();

    // Сбрасываем state при каждом открытии — иначе при повторном вызове
    // юзер увидит застрявший прошлый error / pin.
    useEffect(() => {
        if (visible) {
            setPin('');
            setError(null);
        }
    }, [visible]);

    const shake = () => {
        Animated.sequence([
            Animated.timing(shakeAnim, { toValue: 10, duration: 50, useNativeDriver: true }),
            Animated.timing(shakeAnim, { toValue: -10, duration: 50, useNativeDriver: true }),
            Animated.timing(shakeAnim, { toValue: 10, duration: 50, useNativeDriver: true }),
            Animated.timing(shakeAnim, { toValue: -10, duration: 50, useNativeDriver: true }),
            Animated.timing(shakeAnim, { toValue: 0, duration: 50, useNativeDriver: true }),
        ]).start();
    };

    const handleChange = (value: string) => {
        const digits = value.replace(/[^0-9]/g, '').slice(0, PIN_LENGTH);
        setError(null);
        setPin(digits);

        if (digits.length === PIN_LENGTH) {
            // setTimeout с 200ms — даёт юзеру увидеть, что последняя цифра
            // ввелась, до момента когда модалка закроется (или shake'нет).
            // Тот же интервал, что в CardScreen.handleModalPINChange.
            setTimeout(async () => {
                const isValid = await verifyPIN(digits);
                if (isValid) {
                    setPin('');
                    onSuccess();
                } else {
                    shake();
                    setError('Wrong PIN. Try again.');
                    setPin('');
                }
            }, 200);
        }
    };

    const handleClose = () => {
        setPin('');
        setError(null);
        onCancel();
    };

    return (
        <Modal
            visible={visible}
            transparent
            animationType="fade"
            onRequestClose={handleClose}
        >
            <TouchableOpacity
                style={styles.overlay}
                activeOpacity={1}
                onPress={handleClose}
            >
                {/* Inner container — тоже TouchableOpacity без onPress, чтобы
                    тап ВНУТРИ диалога не закрывал его (Modal closing срабатывает
                    только при тапе на overlay). */}
                <TouchableOpacity activeOpacity={1} onPress={() => { /* noop, stop propagation */ }}>
                    <View style={styles.container}>
                        <Text style={styles.title}>{title}</Text>
                        <Text style={styles.subtitle}>{subtitle}</Text>

                        <TextInput
                            ref={inputRef}
                            value={pin}
                            onChangeText={handleChange}
                            keyboardType="number-pad"
                            maxLength={PIN_LENGTH}
                            secureTextEntry
                            autoFocus
                            style={styles.hiddenInput}
                            caretHidden
                            {...PIN_INPUT_PROPS}
                        />

                        <PINDots
                            pin={pin}
                            pinLength={PIN_LENGTH}
                            error={error}
                            shakeAnimation={shakeAnim}
                            onPress={() => {
                                inputRef.current?.blur();
                                setTimeout(() => inputRef.current?.focus(), 50);
                            }}
                        />

                        {error && <Text style={styles.error}>{error}</Text>}

                        <PinKeypad value={pin} length={PIN_LENGTH} onChange={handleChange} compact />

                        <TouchableOpacity style={styles.cancelButton} onPress={handleClose}>
                            <Text style={styles.cancelText}>Cancel</Text>
                        </TouchableOpacity>
                    </View>
                </TouchableOpacity>
            </TouchableOpacity>
        </Modal>
    );
};

const styles = StyleSheet.create({
    overlay: {
        flex: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        justifyContent: 'center',
        alignItems: 'center',
    },
    container: {
        backgroundColor: COLORS.pureLight,
        borderRadius: 16,
        padding: 24,
        width: 320, // фиксированная ширина — CardScreen использует 85% но
                    // здесь нужно явное значение, т.к. inner-container
                    // ограничивает свою ширину по контенту иначе.
        alignItems: 'center',
    },
    title: {
        fontFamily: 'Inter',
        fontSize: 20,
        fontWeight: '600',
        color: COLORS.deepNavy,
        marginBottom: 8,
        textAlign: 'center',
    },
    subtitle: {
        fontFamily: 'Inter',
        fontSize: 14,
        color: COLORS.blueSteel,
        marginBottom: 24,
        textAlign: 'center',
    },
    error: {
        fontFamily: 'Inter',
        fontSize: 15,
        color: '#E53935',
        marginTop: -8,
        marginBottom: 8,
    },
    cancelButton: {
        marginTop: 8,
        paddingVertical: 10,
        paddingHorizontal: 24,
    },
    cancelText: {
      lineHeight: 22,
      fontFamily: 'Inter',
      fontSize: 16,
      fontWeight: '500',
        color: COLORS.blueSteel,
    },
    hiddenInput: {
        position: 'absolute',
        opacity: 0,
        height: 1,
        width: 1,
    },
});

export default PINConfirmModal;
